import { useState } from 'react';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/DeleteOutlined';
import CheckIcon from '@mui/icons-material/Check';
import {
  GridRowModes,
  GridToolbarContainer,
  GridActionsCellItem,
  GridRowEditStopReasons,
} from '@mui/x-data-grid-premium';
import { Field, FieldArray, useFormikContext } from 'formik';
import dayjs from 'dayjs';
import { v4 as uuidv4 } from 'uuid';

import { dataGridColumns } from '../../utils/helper';
import {
  DataGridIdentifiers,
  FormErrorUI,
  InputUI,
  AddButtonUi,
  DatagridPremiumUi,
  DateTimePickerUI,
} from '../Interface';

const EditToolbar = (props) => {
  const { setRowModesModel, disabled, push, defaultField } = props;
  const { values, validateField } = useFormikContext();

  const handleClick = async () => {
    defaultField.id = uuidv4();
    defaultField.createdAt = dayjs();
    await push(defaultField);

    setRowModesModel((prevModel) => ({
      ...prevModel,
      [defaultField.id]: { mode: GridRowModes.Edit },
    }));

    await validateField(`communication.${values.communication.length ?? 0}.message`);

    const fieldsToValidate = [
      `communication.${values.communication.length ?? 0}.message`,
      `communication.${values.communication.length ?? 0}.calendarVisibleOn`,
    ];

    for (const field of fieldsToValidate) {
      await validateField(field);
    }
  };

  return (
    <GridToolbarContainer>
      <AddButtonUi disabled={disabled} onClick={handleClick} />
    </GridToolbarContainer>
  );
};

const EmployeeCommunication = ({ user, disabled, defaultField }) => {
  const { values, validateField, setFieldValue, errors } = useFormikContext();

  const [rowModesModel, setRowModesModel] = useState({});

  const handleRowEditStop = (params, event) => {
    if (params.reason === GridRowEditStopReasons.rowFocusOut) {
      event.defaultMuiPrevented = true;
    }
  };

  const getValueIndex = (values, id) => {
    return values.communication.findIndex((rc) => rc.id === id);
  };

  const handleEditClick = (id) => () => {
    setRowModesModel({ ...rowModesModel, [id]: { mode: GridRowModes.Edit } });
  };

  const handleSaveClick = async (id) => {
    const valueIndex = getValueIndex(values, id);

    const fieldsToValidate = [`communication.${valueIndex}.message`, `communication.${valueIndex}.calendarVisibleOn`];

    for (const field of fieldsToValidate) {
      await validateField(field);
      if (errors?.communication?.[valueIndex]?.[field.split('.').pop()]) {
        return;
      }
    }

    setRowModesModel((prevModel) => ({
      ...prevModel,
      [id]: { mode: GridRowModes.View },
    }));
  };

  const handleRowModesModelChange = (newRowModesModel) => {
    setRowModesModel(newRowModesModel);
  };

  const columns = (setFieldValue, validateField, errors, remove) => {
    return dataGridColumns([
      {
        field: 'createdAt',
        headerName: 'Dátum.',
        type: 'dateTime',
        valueGetter: (_data, row) => {
          return new Date(row.createdAt);
        },
      },
      {
        field: 'userName',
        headerName: 'Felhasználó',
        type: 'string',
        sortable: false,
      },
      {
        field: 'message',
        headerName: 'Üzenet',
        type: 'string',
        editable: true,
        sortable: false,
        renderEditCell: (data) => {
          const valueIndex = getValueIndex(values, data.id);

          return (
            <Field
              name={`communication.${valueIndex}.message`}
              type="text"
              variant="standard"
              as={InputUI}
              fullWidth
              helperText={<FormErrorUI message={errors?.communication?.[valueIndex]?.message} />}
              onChange={(e) => {
                setFieldValue(`communication.${valueIndex}.message`, e.target.value ?? '');
                data.api.setEditCellValue({
                  id: data.id,
                  field: data.field,
                  value: e.target.value,
                });
              }}
              multiline
              rows={4}
              onBlur={async () => await validateField(`communication.${valueIndex}.message`)}
            />
          );
        },
      },
      {
        field: 'calendarVisibleOn',
        headerName: 'Naptárban megjelenik',
        type: 'dateTime',
        editable: true,
        sortable: false,
        valueGetter: (_data, row) => {
          return row.calendarVisibleOn ? new Date(row.calendarVisibleOn) : null;
        },
        renderCell: (data) => {
          return data.row.calendarVisibleOn ? dayjs(data.row.calendarVisibleOn).format('YYYY.MM.DD. HH:mm') : null;
        },
        renderEditCell: (data) => {
          const valueIndex = getValueIndex(values, data.id);
          return (
            <Field
              name={`communication.${valueIndex}.calendarVisibleOn`}
              component={DateTimePickerUI}
              helperText={<FormErrorUI message={errors?.communication?.[valueIndex]?.calendarVisibleOn} />}
              variant="standard"
              value={dayjs(data.row?.calendarVisibleOn)}
              onChange={(newDate) => {
                const dateObject = newDate.toDate();
                setFieldValue(`communication.${valueIndex}.calendarVisibleOn`, dateObject);
                data.api.setEditCellValue({
                  id: data.id,
                  field: data.field,
                  value: dateObject,
                });
              }}
              onBlur={async () => await validateField(`communication.${valueIndex}.calendarVisibleOn`)}
            />
          );
        },
      },
      {
        field: 'actions',
        type: 'actions',
        headerName: 'Szerk.',
        cellClassName: 'actions',
        sortable: false,
        getActions: ({ id, row }) => {
          const isCurrentUserRow = row.userId === user.userId;
          if (!isCurrentUserRow) {
            return [];
          }

          const isInEditMode = rowModesModel[id]?.mode === GridRowModes.Edit;
          if (isInEditMode) {
            return [
              <GridActionsCellItem
                icon={<CheckIcon />}
                label="Save"
                onClick={() => handleSaveClick(id)}
                color="inherit"
              />,
              <GridActionsCellItem
                icon={<DeleteIcon />}
                label="Delete"
                onClick={() => {
                  const valueIndex = getValueIndex(values, id);
                  return remove(valueIndex);
                }}
                color="inherit"
              />,
            ];
          }

          return [
            <GridActionsCellItem
              icon={<EditIcon />}
              label="Edit"
              onClick={handleEditClick(id)}
              color="inherit"
              disabled={disabled}
            />,
            <GridActionsCellItem
              icon={<DeleteIcon />}
              label="Delete"
              onClick={() => {
                const valueIndex = getValueIndex(values, id);
                return remove(valueIndex);
              }}
              color="inherit"
              disabled={disabled}
            />,
          ];
        },
      },
    ]);
  };

  return (
    <div className="overflow-x-auto">
      <div className="align-middle">
        <div className="overflow-hidden shadow ring-1 ring-black ring-opacity-5 sm:rounded-lg">
          <FieldArray name="communication">
            {({ remove, push }) => (
              <DatagridPremiumUi
                datagridIdentifier={DataGridIdentifiers.communication}
                height={380}
                columns={columns(setFieldValue, validateField, errors, remove)}
                rows={values?.communication ?? []}
                rowModesModel={rowModesModel}
                onRowModesModelChange={handleRowModesModelChange}
                onRowEditStop={handleRowEditStop}
                slots={{
                  toolbar: EditToolbar,
                }}
                slotProps={{
                  toolbar: {
                    disabled,
                    push,
                    defaultField,
                    setRowModesModel,
                  },
                }}
                onCellDoubleClick={(_params, event) => {
                  event.defaultMuiPrevented = true;
                }}
                headerFilters={false}
                getRowHeight={() => 'auto'}
              />
            )}
          </FieldArray>
        </div>
      </div>
    </div>
  );
};

export default EmployeeCommunication;
